import dayjs from 'dayjs';

import { useStableCallback } from 'core/hooks/useStableCallback';

type TWordTemplateItem = {
  type: 'text' | 'highlight';
  text: string;
  error?: true;
};
type TWordTemplate = TWordTemplateItem[];

export const parseWordTemplate = (source: string): TWordTemplate => {
  /**
   * It splits the string by blocks of next types:
   * - {text} - highlighted text
   * - regular text
   */
  const matches = source.match(/(\{[^}]+\}|[^[\]{}]+)/g);
  if (!matches) {
    return [{ type: 'text', text: source, error: true }];
  }

  return matches.map(
    (match): TWordTemplateItem =>
      match[0] === '{'
        ? { type: 'highlight', text: match.slice(1, match.length - 1) }
        : { type: 'text', text: match }
  );
};

export const checkWordTemplate = (source: string): boolean => {
  let bracket: string | null = null;

  return (
    source.split('').every(sym => {
      if (sym === '{') {
        if (bracket) return false;
        bracket = sym;
      } else if (sym === '}') {
        if ('{' !== bracket) return false;
        bracket = null;
      }

      return true;
    }) && bracket === null
  );
};

type TDuration = {
  count: number;
  label: string;
  sign: '+' | '-';
};

export const parseTimeDuration = (date: Date | string): TDuration => {
  const minutes = dayjs(date).diff(dayjs.utc(), 'minute');
  const sign = minutes > 0 ? '+' : '-';

  if (minutes < 120) {
    return { label: 'm', count: minutes, sign };
  }

  if (minutes < 60 * 24 * 2) {
    return { label: 'h', count: Math.floor(minutes / 60), sign };
  }

  const days = Math.floor(minutes / (60 * 24));
  if (days < 365) {
    return { label: 'd', count: days, sign };
  }

  const months = Math.floor(days / 30);
  if (months < 24) {
    return { label: 'mon', count: months, sign };
  }

  const years = Math.floor(months / 12);
  return { label: 'y', count: years, sign };
};

type ParsedNotePiece = { type: 'regular' | 'secret'; v: string };

export function parseNote(source: string): ParsedNotePiece[] {
  const nodes: ParsedNotePiece[] = [{ type: 'regular', v: '' }];
  const last = () => nodes[nodes.length - 1];

  for (const sym of source) {
    switch (sym) {
      case '[':
        if (last().type === 'regular') {
          nodes.push({ type: 'secret', v: '' });
        } else {
          last().v += sym;
        }
        break;

      case ']':
        if (last().type === 'secret') {
          nodes.push({ type: 'regular', v: '' });
        } else {
          last().v += sym;
        }
        break;

      default:
        last().v += sym;
        break;
    }
  }

  return nodes.filter(({ v }) => v.length > 0);
}

/**
 * Word.note and Word.valueNote may contain pieces of text in square brackets.
 * Such text is considered as a hint and should be displayed only when the card
 * is finished. This method removes [] when the word is finished, and removes
 * the whole text in [] when the word is not finished.
 */
export function formatNote(note: string, finished: boolean): React.ReactNode {
  return parseNote(note).map(({ type, v }, i) => (
    <span
      style={type === 'secret' && !finished ? { visibility: 'hidden' } : {}}
      key={i}
    >
      {prettifyText(v)}
    </span>
  ));
}

export function useFormatedTextChange<
  T extends HTMLInputElement | HTMLTextAreaElement,
>(propOnChange?: React.ChangeEventHandler<T>) {
  return useStableCallback(function onChange(event: React.KeyboardEvent<T>) {
    if (event.key !== '{') return;

    const target = event.target as HTMLInputElement;
    const s = target.selectionStart ?? 0;
    const e = target.selectionEnd ?? 0;
    if (e - s === 0) return;

    event.preventDefault();

    target.value =
      target.value.substring(0, s) +
      '{' +
      target.value.substring(s, e) +
      '}' +
      target.value.substring(e);
    target.selectionStart = e + 1;
    target.selectionEnd = target.selectionStart;

    propOnChange?.(event as unknown as React.ChangeEvent<T>);
  });
}

export function prettifyText(text: string): string {
  return text
    .replace(/<->/g, '↔')
    .replace(/->/g, '→')
    .replace(/<-/g, '←')
    .replace(/>=/g, '≥')
    .replace(/<=/g, '≤')
    .replace(/!=/g, '≠');
}
